import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import {
  ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminFailedFlowQuery,
  type AdminProjectionFreshness,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import {
  loadFailedFlowIncidentLogArtifactMap,
  loadFailedFlowMediaArtifactMap,
  searchFailedFlows,
} from "./failed-flows-use-cases";
import { toFailedFlowsViewModel, type FailedFlowsViewModel } from "./failed-flows-view-model";

export type FailedFlowsScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminFailedFlowQuery;
  skip?: boolean;
};

export type FailedFlowsScreenModel = AdminScreenState<FailedFlowsViewModel>;

function createDefaultFailedFlowQuery(): AdminFailedFlowQuery {
  return {
    timeRange: {
      from: ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
      to: new Date().toISOString(),
    },
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  };
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(dependencies: FailedFlowsScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    query: dependencies.query ?? null,
  });
}

export async function resolveFailedFlowsScreenModel(
  dependencies: FailedFlowsScreenModelDependencies,
): Promise<FailedFlowsScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.failed_flows.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "Failed flows read permission denied.",
        ko: "오류 흐름 조회 권한이 없습니다.",
      },
      "admin_failed_flows_permission_denied",
    );
  }

  try {
    const envelope = await searchFailedFlows(
      dependencies.queryPort,
      dependencies.query ?? createDefaultFailedFlowQuery(),
    );
    const matchedArtifactsByFlowId = await loadFailedFlowMediaArtifactMap(
      dependencies.queryPort,
      envelope.data.items,
    );
    const matchedIncidentLogArtifactsByFlowId = await loadFailedFlowIncidentLogArtifactMap(
      dependencies.queryPort,
      envelope.data.items,
    );

    if (envelope.data.items.length === 0) {
      return {
        status: "empty",
      };
    }

    const viewModel = toFailedFlowsViewModel(
      envelope.data,
      envelope.meta,
      matchedArtifactsByFlowId,
      matchedIncidentLogArtifactsByFlowId,
      dependencies.runtimeConfig,
      dependencies.locale ?? "ko",
    );
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useFailedFlowsScreenModel(
  dependencies: FailedFlowsScreenModelDependencies,
): FailedFlowsScreenModel {
  const [state, setState] = useState<FailedFlowsScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);

  useEffect(() => {
    if (dependencies.skip) {
      setState(createIdleAdminScreenState());
      return;
    }

    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveFailedFlowsScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependencies.skip, dependenciesKey]);

  return state;
}
